// MARK://dhk8/admin-shell/pages/modules/ComboDetailPage
// Detalle / edición de un combo → dhk8-catalog-combos.
//   - Form de cabecera: name, priceCents (en MXN), description, isActive.
//     Guardado on-blur por campo (parche fino — minimiza colisiones).
//   - ImageUploader: imagen del combo (updateCombo acepta imageUrl).
//   - Panel ComboItems editable: lista con cantidad InlineEdit, "Agregar producto"
//     (EntityCombobox para producto fijo o slot por categoría), editar in-place
//     (updateComboItem: cantidad/opcional/orden + producto o categoría con XOR) y quitar.
//     Productos se resuelven contra dhk8-erp-products (listProducts → solo
//     type=PRODUCT; los insumos/INGREDIENT no entran al selector del combo).
//   - Eliminar combo (soft-delete, ConfirmDialog) → vuelve a la lista.
//   - Avisos con useToast (sin alert/confirm nativos).
//   - Stubs: si es uno de los conocidos ("molletes con queso" / "torta") o si
//     no tiene items aún, se muestra un banner "Necesita configuración".
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Trash2, Plus, AlertTriangle, Package, Pencil } from 'lucide-react'
import * as combos from '../../lib/clients/catalog-combos.js'
import * as cat from '../../lib/clients/catalog.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import ImageUploader from '../../components/ImageUploader.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import MoneyInput from '../../components/MoneyInput.js'
import InlineEdit from '../../components/InlineEdit.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

function mxn(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

/** Subtítulo discreto de sección — marca NintaiGO: mono, uppercase, terciario. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--pos-fg-tertiary)' }}>{children}</div>
  )
}

export default function ComboDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const combo = useAsync((s) => combos.getCombo(id, s), [id])
  // Combo items reference products by productId. El selector "Agregar producto"
  // debe ofrecer SOLO productos vendibles (type=PRODUCT) — los insumos/INGREDIENT
  // no son parte de un combo de venta. listProducts excluye INGREDIENT.
  const products = useAsync((s) => cat.listProducts(s))

  // Categorías para los slots por categoría de ComboItem (A7 §1.6).
  const categories = useAsync((s) => cat.listCategories(s))

  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<{ name: string; description: string; priceMxn: number; active: boolean; pricingMode: combos.ComboPricingMode; validFrom: string; validUntil: string } | null>(null)

  // Convierte un ISO (o null) al value que espera <input type="date"> (YYYY-MM-DD).
  const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')

  useEffect(() => {
    if (combo.data && draft === null) {
      setDraft({
        name: combo.data.name,
        description: combo.data.description ?? '',
        priceMxn: combo.data.priceCents / 100,
        active: combo.data.active,
        pricingMode: combo.data.pricingMode,
        validFrom: toDateInput(combo.data.validFrom),
        validUntil: toDateInput(combo.data.validUntil),
      })
    }
  }, [combo.data, draft])

  // add-item sheet — A7 §1.6: un item es un producto fijo o un slot por categoría (XOR).
  const [addingItem, setAddingItem] = useState(false)
  const [itemForm, setItemForm] = useState<{ source: 'product' | 'category'; product: Product | null; categoryId: string; allowedProductIds: string[]; quantity: number; isOptional: boolean }>({ source: 'product', product: null, categoryId: '', allowedProductIds: [], quantity: 1, isOptional: false })
  const [itemError, setItemError] = useState<string | null>(null)

  // edit-item sheet — edición in-place (updateComboItem). Conserva el id del item.
  const [editingItem, setEditingItem] = useState<combos.ComboItem | null>(null)
  const [editItemForm, setEditItemForm] = useState<{ source: 'product' | 'category'; product: Product | null; categoryId: string; allowedProductIds: string[]; quantity: number; isOptional: boolean; sortOrder: number }>({ source: 'product', product: null, categoryId: '', allowedProductIds: [], quantity: 1, isOptional: false, sortOrder: 0 })
  const [editItemError, setEditItemError] = useState<string | null>(null)

  const c = combo.data
  const stub = c ? combos.isStub(c) : false
  const productName = (pid: string | null) => (pid == null ? '(producto)' : (products.data ?? []).find((p) => p.id === pid)?.name ?? '(producto no encontrado)')
  const categoryName = (cid: string | null) => (cid == null ? '' : (categories.data ?? []).find((x) => x.id === cid)?.name ?? '(categoría no encontrada)')

  // Búsqueda local para el EntityCombobox de productos (catálogo ya cargado en memoria).
  async function searchProducts(query: string): Promise<Product[]> {
    const all = products.data ?? []
    const term = query.trim().toLowerCase()
    const matched = term ? all.filter((p) => p.name.toLowerCase().includes(term) || (p.sku ?? '').toLowerCase().includes(term)) : all
    return matched.slice(0, 50)
  }

  async function patch(patchBody: Parameters<typeof combos.updateCombo>[1], errMsg: string) {
    if (!c) return
    setBusy(true)
    try { await combos.updateCombo(c.id, patchBody); combo.reload() }
    catch (e) { toast(e instanceof Error ? e.message : errMsg, 'danger') }
    finally { setBusy(false) }
  }

  async function saveName() {
    if (!c || !draft) return
    const next = draft.name.trim()
    if (next === c.name) return
    if (!next) { toast('El nombre es obligatorio.', 'warn'); setDraft({ ...draft, name: c.name }); return }
    await patch({ name: next }, 'No se pudo guardar el nombre.')
  }
  async function saveDescription() {
    if (!c || !draft) return
    const next = draft.description.trim()
    if ((c.description ?? '') === next) return
    await patch({ description: next === '' ? null : next }, 'No se pudo guardar la descripción.')
  }
  async function savePrice() {
    if (!c || !draft) return
    const cents = Math.round(draft.priceMxn * 100)
    if (cents === c.priceCents) return
    if (cents < 0) { toast('El precio no puede ser negativo.', 'warn'); setDraft({ ...draft, priceMxn: c.priceCents / 100 }); return }
    await patch({ priceCents: cents }, 'No se pudo guardar el precio.')
  }
  async function toggleActive() {
    if (!c || !draft) return
    const next = !draft.active
    setDraft({ ...draft, active: next })
    await patch({ active: next }, 'No se pudo cambiar el estado.')
  }
  async function savePricingMode(mode: combos.ComboPricingMode) {
    if (!c || !draft) return
    setDraft({ ...draft, pricingMode: mode })
    if (mode === c.pricingMode) return
    await patch({ pricingMode: mode }, 'No se pudo guardar el modo de precio.')
  }
  async function saveValidFrom() {
    if (!c || !draft) return
    const next = draft.validFrom ? new Date(draft.validFrom).toISOString() : null
    if ((c.validFrom ?? null) === next) return
    await patch({ validFrom: next }, 'No se pudo guardar la vigencia.')
  }
  async function saveValidUntil() {
    if (!c || !draft) return
    const next = draft.validUntil ? new Date(draft.validUntil).toISOString() : null
    if ((c.validUntil ?? null) === next) return
    await patch({ validUntil: next }, 'No se pudo guardar la vigencia.')
  }
  async function saveImage(url: string | null) {
    if (!c) return
    await patch({ imageUrl: url }, 'No se pudo guardar la imagen.')
  }

  async function saveAddItem() {
    if (!c) return
    setItemError(null)
    if (itemForm.source === 'product' && !itemForm.product) { setItemError('Selecciona un producto.'); return }
    if (itemForm.source === 'category' && !itemForm.categoryId) { setItemError('Selecciona una categoría.'); return }
    if (itemForm.quantity <= 0) { setItemError('La cantidad debe ser mayor a cero.'); return }
    setBusy(true)
    try {
      await combos.addComboItem(c.id, {
        quantity: itemForm.quantity,
        isOptional: itemForm.isOptional,
        ...(itemForm.source === 'product'
          ? { productId: itemForm.product!.id }
          : { categoryId: itemForm.categoryId, ...(itemForm.allowedProductIds.length > 0 ? { allowedProductIds: itemForm.allowedProductIds } : {}) }),
      })
      setAddingItem(false)
      setItemForm({ source: 'product', product: null, categoryId: '', allowedProductIds: [], quantity: 1, isOptional: false })
      combo.reload()
    } catch (e) {
      setItemError(e instanceof Error ? e.message : 'No se pudo agregar el producto.')
    } finally { setBusy(false) }
  }

  async function changeItemQty(item: combos.ComboItem, quantity: number) {
    if (!c) return
    if (!(quantity > 0)) throw new Error('La cantidad debe ser mayor a cero.')
    if (quantity === item.quantity) return
    await combos.updateComboItem(c.id, item, { quantity })
    combo.reload()
  }

  function openEditItem(item: combos.ComboItem) {
    setEditItemForm({
      source: item.categoryId ? 'category' : 'product',
      product: item.productId ? (products.data ?? []).find((p) => p.id === item.productId) ?? null : null,
      categoryId: item.categoryId ?? '',
      allowedProductIds: [...item.allowedProductIds],
      quantity: item.quantity,
      isOptional: item.isOptional,
      sortOrder: item.sortOrder,
    })
    setEditItemError(null); setEditingItem(item)
  }

  async function saveEditItem() {
    if (!c || !editingItem) return
    setEditItemError(null)
    if (editItemForm.source === 'product' && !editItemForm.product) { setEditItemError('Selecciona un producto.'); return }
    if (editItemForm.source === 'category' && !editItemForm.categoryId) { setEditItemError('Selecciona una categoría.'); return }
    if (editItemForm.quantity <= 0) { setEditItemError('La cantidad debe ser mayor a cero.'); return }
    setBusy(true)
    try {
      await combos.updateComboItem(c.id, editingItem, {
        quantity: editItemForm.quantity,
        isOptional: editItemForm.isOptional,
        sortOrder: editItemForm.sortOrder,
        // XOR — el item queda como producto fijo O slot por categoría (nunca ambos).
        ...(editItemForm.source === 'product'
          ? { productId: editItemForm.product!.id, categoryId: null }
          : { categoryId: editItemForm.categoryId, productId: null, allowedProductIds: editItemForm.allowedProductIds }),
      })
      setEditingItem(null); combo.reload(); toast('Producto del combo actualizado', 'success')
    } catch (e) {
      setEditItemError(e instanceof Error ? e.message : 'No se pudo actualizar el producto.')
    } finally { setBusy(false) }
  }

  async function removeItem(itemId: string) {
    if (!c) return
    setBusy(true)
    try { await combos.removeComboItem(c.id, itemId); combo.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo quitar el producto.', 'danger') }
    finally { setBusy(false) }
  }

  async function destroy() {
    if (!c) return
    const ok = await confirm({
      title: '¿Eliminar combo?',
      message: `Se eliminará “${c.name}”. Se puede revertir desde el backend (soft delete).`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    setBusy(true)
    try {
      await combos.deleteCombo(c.id)
      navigate('/combos')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo eliminar.', 'danger')
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/combos')} aria-label="Volver">
            <ChevronLeft size={18} />
          </button>
          <div>
            <h1 className="adm-page__title">{c?.name ?? 'Combo'}</h1>
            <p className="adm-page__sub">Catálogo · combo</p>
          </div>
        </div>
        {c && (
          <div className="adm-page__actions">
            <button className="adm-btn adm-btn--danger" disabled={busy} onClick={destroy}>
              <Trash2 size={15} /> Eliminar combo
            </button>
          </div>
        )}
      </div>

      {asyncState(combo.loading, combo.error, combo.reload)}

      {c && draft && (
        <>
          {stub && (
            <div className="adm-banner adm-banner--warn" role="status" style={{ marginBottom: 16 }}>
              <AlertTriangle size={15} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6 }} />
              <strong>Necesita configuración</strong> — agrégale productos al panel de abajo
              {c.items.length === 0 ? ' (todavía no tiene ningún ítem)' : ''}.
            </div>
          )}

          <div className="adm-card adm-card--pad" style={{ marginBottom: 16, display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <label className="adm-field">
              <span className="adm-field__label">Nombre</span>
              <input
                className="adm-input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                onBlur={saveName}
                disabled={busy}
              />
            </label>
            <label className="adm-field" onBlur={savePrice}>
              <span className="adm-field__label">Precio (MXN)</span>
              <MoneyInput
                valueCents={Math.round(draft.priceMxn * 100)}
                onChangeCents={(c) => setDraft({ ...draft, priceMxn: c / 100 })}
                disabled={busy}
              />
            </label>
            <label className="adm-field" style={{ gridColumn: '1 / -1' }}>
              <span className="adm-field__label">Descripción</span>
              <input
                className="adm-input"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                onBlur={saveDescription}
                disabled={busy}
                placeholder="Resumen visible para el comerciante (opcional)"
              />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Modo de precio</span>
              <select className="adm-select" value={draft.pricingMode} disabled={busy} onChange={(e) => savePricingMode(e.target.value as combos.ComboPricingMode)}>
                <option value="FIXED_PRICE">Precio fijo</option>
                <option value="SUM_WITH_DISCOUNT">Suma con descuento</option>
              </select>
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Válido desde</span>
              <input className="adm-input" type="date" value={draft.validFrom} disabled={busy} onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })} onBlur={saveValidFrom} />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Válido hasta</span>
              <input className="adm-input" type="date" value={draft.validUntil} disabled={busy} onChange={(e) => setDraft({ ...draft, validUntil: e.target.value })} onBlur={saveValidUntil} />
            </label>
            <label className="adm-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={draft.active}
                onChange={toggleActive}
                disabled={busy}
              />
              <span className="adm-field__label" style={{ margin: 0 }}>Combo activo (se puede vender)</span>
            </label>
          </div>

          {/* ── Imagen ───────────────────────────────────────────────────── */}
          <div className="adm-card adm-card--pad" style={{ marginBottom: 16, display: 'grid', gap: 12, maxWidth: 320 }}>
            <SectionTitle>Imagen</SectionTitle>
            <ImageUploader value={c.imageUrl} onChange={(url) => void saveImage(url)} />
          </div>

          <div className="adm-card adm-card--pad0">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>Productos del combo</h2>
              <button
                className="adm-btn adm-btn--primary adm-btn--sm"
                onClick={() => { setItemForm({ source: 'product', product: null, categoryId: '', allowedProductIds: [], quantity: 1, isOptional: false }); setItemError(null); setAddingItem(true) }}
                disabled={busy}
              >
                <Plus size={15} /> Agregar producto
              </button>
            </div>
            <div className="adm-list">
              {c.items.length === 0 && (
                <div className="adm-empty">Aún no hay productos. Agrega al menos uno para que el combo se pueda vender.</div>
              )}
              {c.items.map((it) => (
                <div key={it.id} className="adm-row">
                  <div className="adm-row__main">
                    <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Package size={14} aria-hidden style={{ opacity: 0.6 }} />
                      {it.categoryId ? `Categoría: ${categoryName(it.categoryId)}` : productName(it.productId)}
                      {it.isOptional && <span className="adm-badge adm-badge--info">Opcional</span>}
                    </div>
                    <div className="adm-row__meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Cantidad:</span>
                      <InlineEdit
                        value={it.quantity}
                        type="number"
                        onSave={(v) => changeItemQty(it, Math.round(Number(v)))}
                      />
                      {it.categoryId && it.allowedProductIds.length > 0 ? <span>· {it.allowedProductIds.length} producto(s) permitido(s)</span> : null}
                    </div>
                  </div>
                  <div className="adm-row__tail" style={{ display: 'inline-flex', gap: 4 }}>
                    <button
                      className="adm-btn adm-btn--ghost adm-btn--sm"
                      disabled={busy}
                      onClick={() => openEditItem(it)}
                      aria-label="Editar"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger"
                      disabled={busy}
                      onClick={() => removeItem(it.id)}
                      aria-label="Quitar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="adm-card adm-card--pad" style={{ marginTop: 16, fontSize: 13, color: 'var(--pos-fg-tertiary)' }}>
            Precio fijo del combo: <strong style={{ color: 'var(--pos-fg)' }}>{mxn(c.priceCents)}</strong> · {c.items.length} producto{c.items.length === 1 ? '' : 's'} · {c.active ? 'activo' : 'inactivo'}
          </div>

          {/* ── Historial ────────────────────────────────────────────────── */}
          <div className="adm-card adm-card--pad0" style={{ marginTop: 16 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>Historial</h2>
            </div>
            <div style={{ padding: 16 }}>
              <AuditTrail entityId={c.id} />
            </div>
          </div>
        </>
      )}

      <Sheet
        open={addingItem}
        title="Agregar producto al combo"
        onClose={() => { setAddingItem(false); setItemError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAddingItem(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveAddItem} disabled={busy || (itemForm.source === 'product' ? !itemForm.product : !itemForm.categoryId)}>
            {busy ? 'Guardando…' : 'Agregar'}
          </button>
        </>}
      >
        {itemError && <div className="adm-banner adm-banner--danger" role="alert">{itemError}</div>}
        <div role="group" aria-label="Tipo de item" style={{ display: 'inline-flex', gap: 4, marginBottom: 8 }}>
          <button type="button" className={`adm-btn adm-btn--sm${itemForm.source === 'product' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setItemForm({ ...itemForm, source: 'product' })} aria-pressed={itemForm.source === 'product'}>Producto fijo</button>
          <button type="button" className={`adm-btn adm-btn--sm${itemForm.source === 'category' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setItemForm({ ...itemForm, source: 'category' })} aria-pressed={itemForm.source === 'category'}>Slot por categoría</button>
        </div>
        {itemForm.source === 'product' ? (
          <label className="adm-field">
            <span className="adm-field__label">Producto</span>
            <EntityCombobox<Product>
              value={itemForm.product}
              onChange={(p) => setItemForm({ ...itemForm, product: p })}
              fetchOptions={searchProducts}
              renderOption={(p) => <span>{p.name}{p.sku ? <span className="adm-muted"> · {p.sku}</span> : null}</span>}
              getOptionValue={(p) => p.id}
              getOptionLabel={(p) => p.name}
              placeholder="Buscar producto…"
              allowClear
            />
          </label>
        ) : (
          <>
            <label className="adm-field">
              <span className="adm-field__label">Categoría</span>
              <select
                className="adm-select"
                value={itemForm.categoryId}
                onChange={(e) => setItemForm({ ...itemForm, categoryId: e.target.value })}
                aria-label="Categoría"
              >
                <option value="">— Selecciona categoría —</option>
                {(categories.data ?? []).map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
              </select>
            </label>
            <div className="adm-field">
              <span className="adm-field__label">Productos permitidos (opcional)</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                {(products.data ?? []).map((p) => (
                  <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={itemForm.allowedProductIds.includes(p.id)}
                      onChange={() => setItemForm((f) => ({ ...f, allowedProductIds: f.allowedProductIds.includes(p.id) ? f.allowedProductIds.filter((x) => x !== p.id) : [...f.allowedProductIds, p.id] }))}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
              <span className="adm-row__meta" style={{ marginTop: 4 }}>Si no marcas ninguno, el slot acepta cualquier producto de la categoría.</span>
            </div>
          </>
        )}
        <label className="adm-field">
          <span className="adm-field__label">Cantidad</span>
          <input
            className="adm-input"
            type="number"
            min={1}
            step="1"
            value={itemForm.quantity}
            onChange={(e) => setItemForm({ ...itemForm, quantity: Number(e.target.value) })}
          />
        </label>
        <label className="adm-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            checked={itemForm.isOptional}
            onChange={(e) => setItemForm({ ...itemForm, isOptional: e.target.checked })}
          />
          <span className="adm-field__label" style={{ margin: 0 }}>Es opcional dentro del combo</span>
        </label>
      </Sheet>

      {/* Editar producto del combo IN-PLACE (updateComboItem) */}
      <Sheet
        open={editingItem != null}
        title="Editar producto del combo"
        onClose={() => { setEditingItem(null); setEditItemError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditingItem(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEditItem} disabled={busy || (editItemForm.source === 'product' ? !editItemForm.product : !editItemForm.categoryId)}>
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </>}
      >
        {editItemError && <div className="adm-banner adm-banner--danger" role="alert">{editItemError}</div>}
        <div role="group" aria-label="Tipo de item" style={{ display: 'inline-flex', gap: 4, marginBottom: 8 }}>
          <button type="button" className={`adm-btn adm-btn--sm${editItemForm.source === 'product' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setEditItemForm({ ...editItemForm, source: 'product' })} aria-pressed={editItemForm.source === 'product'}>Producto fijo</button>
          <button type="button" className={`adm-btn adm-btn--sm${editItemForm.source === 'category' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setEditItemForm({ ...editItemForm, source: 'category' })} aria-pressed={editItemForm.source === 'category'}>Slot por categoría</button>
        </div>
        {editItemForm.source === 'product' ? (
          <label className="adm-field">
            <span className="adm-field__label">Producto</span>
            <EntityCombobox<Product>
              value={editItemForm.product}
              onChange={(p) => setEditItemForm({ ...editItemForm, product: p })}
              fetchOptions={searchProducts}
              renderOption={(p) => <span>{p.name}{p.sku ? <span className="adm-muted"> · {p.sku}</span> : null}</span>}
              getOptionValue={(p) => p.id}
              getOptionLabel={(p) => p.name}
              placeholder="Buscar producto…"
              allowClear
            />
          </label>
        ) : (
          <>
            <label className="adm-field">
              <span className="adm-field__label">Categoría</span>
              <select
                className="adm-select"
                value={editItemForm.categoryId}
                onChange={(e) => setEditItemForm({ ...editItemForm, categoryId: e.target.value })}
                aria-label="Categoría"
              >
                <option value="">— Selecciona categoría —</option>
                {(categories.data ?? []).map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
              </select>
            </label>
            <div className="adm-field">
              <span className="adm-field__label">Productos permitidos (opcional)</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                {(products.data ?? []).map((p) => (
                  <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={editItemForm.allowedProductIds.includes(p.id)}
                      onChange={() => setEditItemForm((f) => ({ ...f, allowedProductIds: f.allowedProductIds.includes(p.id) ? f.allowedProductIds.filter((x) => x !== p.id) : [...f.allowedProductIds, p.id] }))}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
              <span className="adm-row__meta" style={{ marginTop: 4 }}>Si no marcas ninguno, el slot acepta cualquier producto de la categoría.</span>
            </div>
          </>
        )}
        <label className="adm-field">
          <span className="adm-field__label">Cantidad</span>
          <input
            className="adm-input"
            type="number"
            min={1}
            step="1"
            value={editItemForm.quantity}
            onChange={(e) => setEditItemForm({ ...editItemForm, quantity: Number(e.target.value) })}
          />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Orden</span>
          <input
            className="adm-input"
            type="number"
            min={0}
            step="1"
            value={editItemForm.sortOrder}
            onChange={(e) => setEditItemForm({ ...editItemForm, sortOrder: Number(e.target.value) })}
          />
        </label>
        <label className="adm-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            checked={editItemForm.isOptional}
            onChange={(e) => setEditItemForm({ ...editItemForm, isOptional: e.target.checked })}
          />
          <span className="adm-field__label" style={{ margin: 0 }}>Es opcional dentro del combo</span>
        </label>
      </Sheet>
    </div>
  )
}
